import { useState } from 'react';
import { Board } from './Board';
import { PieceTray } from './PieceTray';
import { MiniTray } from './MiniTray';
import { WinScreen } from './WinScreen';
import { canPlace, hasValidMove, isGameOver } from '@otrio/engine';
import { COLOR_HEX, COLOR_LABELS, Color, GameState, RingSize } from '@otrio/engine';
import { ClientMessage } from '../protocol';

interface GameScreenProps {
  gameState: GameState;
  turnNumber: number;
  /** Colors this client controls. Empty = spectator / single-device mode (can play all). */
  yourColors: Color[];
  roomCode: string | null;
  onSendMessage: (msg: ClientMessage) => void;
  onTurnEnd: (newState: GameState) => void;
  onDraw: (finalState: GameState) => void;
  onPlayAgain?: () => void;
  suppressDrawOverlay?: boolean;
  onExit?: () => void;
}

export function GameScreen({
  gameState, turnNumber, yourColors, roomCode, onSendMessage, onTurnEnd, onDraw, onPlayAgain, suppressDrawOverlay, onExit,
}: GameScreenProps) {
  const [selectedSize, setSelectedSize] = useState<RingSize | null>(null);
  const [dragSize, setDragSize] = useState<RingSize | null>(null);

  const { players, currentPlayerIndex, board, winLine } = gameState;
  const currentPlayer = players[currentPlayerIndex];
  const canMove = hasValidMove(board, currentPlayer);
  const hex = COLOR_HEX[currentPlayer.color];
  const winnerPlayer = gameState.winner ? players.find(p => p.color === gameState.winner) : null;

  // Collect all playerIds this device controls (handles 2-player paired mode too)
  const myPlayerIds = new Set(
    players.filter(p => yourColors.includes(p.color)).map(p => p.playerId)
  );

  // Spectator / single-device fallback: empty yourColors means play everything
  const isMyTurn = yourColors.length === 0 || myPlayerIds.has(currentPlayer.playerId);

  // Opponents: players not covered by any of my playerIds, de-duped by playerId
  const seenPlayerIds = new Set<number>();
  const opponents = players
    .filter((p, i) => {
      if (i === currentPlayerIndex) return false;
      if (myPlayerIds.has(p.playerId)) return false;
      if (seenPlayerIds.has(p.playerId)) return false;
      seenPlayerIds.add(p.playerId);
      return true;
    });

  function attemptPlace(cellIndex: number, size: RingSize | null) {
    if (!size) return;
    if (!isMyTurn) return;
    if (!canPlace(board, cellIndex, size)) return;
    if (currentPlayer.pieces[size] <= 0) return;
    setSelectedSize(null);
    setDragSize(null);
    if (roomCode) {
      onSendMessage({ type: 'PLACE_PIECE', roomCode, cellIndex, size });
    }
  }

  function handleSkip() {
    if (!isMyTurn || !roomCode) return;
    onSendMessage({ type: 'SKIP_TURN', roomCode });
  }

  return (
    <div className="game-screen">

      {/* Opponents strip above the board */}
      <div className="opponents-strip">
        {opponents.map(p => <MiniTray key={p.color} player={p} />)}
        {onExit && (
          <button className="game-exit-btn" onClick={onExit} title="Leave game">✕ Leave</button>
        )}
      </div>

      {/* Board */}
      <div className="board-container">
        <Board
          board={board}
          selectedSize={isMyTurn && !currentPlayer.isBot ? selectedSize : null}
          currentColor={currentPlayer.color}
          winner={gameState.winner}
          winLine={winLine}
          onDrop={(cellIndex) => attemptPlace(cellIndex, dragSize ?? selectedSize)}
          onCellClick={(cellIndex) => attemptPlace(cellIndex, selectedSize)}
        />
      </div>

      {/* Current player bar */}
      <div className="current-player-bar" style={{ borderTopColor: hex, borderBottomColor: hex }}>
        <div className="current-player-rings">
          <svg width={40} height={40} viewBox="0 0 40 40">
            <circle cx={20} cy={20} r={16} fill="none" stroke={hex} strokeWidth={6} />
            <circle cx={20} cy={20} r={9}  fill="none" stroke={hex} strokeWidth={5} opacity={0.65} />
            <circle cx={20} cy={20} r={3.5} fill="none" stroke={hex} strokeWidth={4} opacity={0.35} />
          </svg>
        </div>
        <div className="current-player-info">
          <span className="current-player-name">{currentPlayer.name}</span>
          <span className="current-player-meta" style={{ color: hex }}>
            {COLOR_LABELS[currentPlayer.color]} · Turn {turnNumber}
          </span>
        </div>
        {!canMove && <span className="no-moves-badge">No moves</span>}
      </div>

      {/* Piece tray */}
      {winnerPlayer && onPlayAgain ? (
        <WinScreen winner={gameState.winner!} winnerName={winnerPlayer.name} onPlayAgain={onPlayAgain} />
      ) : canMove ? (
        currentPlayer.isBot || !isMyTurn ? (
          <div className="bot-thinking">
            <span className="bot-thinking-dot" />
            <span className="bot-thinking-dot" />
            <span className="bot-thinking-dot" />
            <span className="bot-thinking-label">
              {currentPlayer.isBot ? `${currentPlayer.name} is thinking…` : `Waiting for ${currentPlayer.name}…`}
            </span>
          </div>
        ) : (
          <PieceTray
            player={currentPlayer}
            selectedSize={selectedSize}
            onSelectSize={(size) => setSelectedSize(prev => prev === size ? null : size)}
            onDragStart={(size) => { setDragSize(size); setSelectedSize(size); }}
            onDragEnd={() => setDragSize(null)}
          />
        )
      ) : (
        <div className="skip-container">
          <p className="skip-message">All of {currentPlayer.name}'s remaining pieces are blocked.</p>
          {!currentPlayer.isBot && isMyTurn && (
            <button className="skip-btn" onClick={handleSkip}>Skip Turn</button>
          )}
        </div>
      )}

      {isGameOver(gameState) && !suppressDrawOverlay && (
        <div className="draw-overlay">
          <p>No more moves — it's a draw!</p>
          <button onClick={() => onDraw(gameState)}>See Final Board</button>
        </div>
      )}
    </div>
  );

}
